import React from 'react';
import * as yup from 'yup';

import { Button } from '../Button';
import { Col } from '../Col';
import { Div } from '../HTMLElement';
import { t } from '../i18n/i18n';
import { Row } from '../Row';
import { CustomerFormHelper, CustomerFormProps } from './CustomerForm';
import { FieldArray } from './FieldArray';
import { PasswordInput, TextInput } from './fields/Input';
import { Checkbox, Radios } from './fields/RadioCheck';
import { getInitialString } from './Form';
import { FormGroup } from './FormGroup';
// eslint-disable-next-line @typescript-eslint/ban-types
type AdditionalPropertiesFormValues = {};

// eslint-disable-next-line @typescript-eslint/ban-types
type AdditionalPropertiesValidationSchema = yup.ObjectSchema<{}>;

// eslint-disable-next-line @typescript-eslint/ban-types
type AdditionalPropertiesChildrenValidationSchema = yup.ObjectSchema<{}>;

// eslint-disable-next-line @typescript-eslint/ban-types
type CustomPropertiesChildrenFormValues = {};
// eslint-disable-next-line @typescript-eslint/ban-types
type CustomPropertiesFormValues = {};

// eslint-disable-next-line @typescript-eslint/ban-types
type AdditionalPropertiesChildrenFormValues = {};

// eslint-disable-next-line @typescript-eslint/ban-types
type CustomPropertiesValidationSchema = yup.ObjectSchema<{}>;

// eslint-disable-next-line @typescript-eslint/ban-types
type CustomPropertiesChildrenValidationSchema = yup.ObjectSchema<{}>;

export type CustomerFormEProps = Omit<
  CustomerFormProps<
    AdditionalPropertiesFormValues,
    AdditionalPropertiesValidationSchema,
    CustomPropertiesFormValues,
    CustomPropertiesValidationSchema,
    AdditionalPropertiesChildrenFormValues,
    AdditionalPropertiesChildrenValidationSchema,
    CustomPropertiesChildrenFormValues,
    CustomPropertiesChildrenValidationSchema
  >,
  | 'additionalPropertiesInitialValues'
  | 'additionalProperties'
  | 'additionalPropertiesValidationSchema'
  | 'customPropertiesInitialValues'
  | 'customPropertiesValidationSchema'
  | 'validationSchemaSelector'
  | 'additionalPropertiesChildrenInitialValues'
  | 'additionalPropertiesChildren'
  | 'additionalPropertiesChildrenValidationSchema'
  | 'customPropertiesChildrenInitialValues'
  | 'customPropertiesChildrenValidationSchema'
  | 'validationSchemaChildrenSelector'
>;

export const CustomerFormE = (props: CustomerFormEProps) => {
  return (
    <CustomerFormHelper<
      AdditionalPropertiesFormValues,
      AdditionalPropertiesValidationSchema,
      CustomPropertiesFormValues,
      CustomPropertiesValidationSchema,
      AdditionalPropertiesChildrenFormValues,
      AdditionalPropertiesChildrenValidationSchema,
      CustomPropertiesChildrenFormValues,
      CustomPropertiesChildrenValidationSchema
    >
      {...props}
      initialValues={{
        enableBusinessData: 'true',
        profile: 'BUSINESS',
      }}
      maxChildren={0}
      maxPhoneNumbers={1}
      minChildren={0}
      minPhoneNumbers={1}
      validationSchemaSelector={{
        birthdate: false,
        children: { birthdate: false, name: false, sex: false, surname: false },
        name: true,
        phoneNumber: true,
        profile: true,
        sex: false,
        surname: true,
      }}
    >
      {({ content, fieldsHelper, formik, mode }) => (
        <>
          <Div className="mb-3 pb-3">
            <Row gap={3}>
              <Col size={6}>
                <FormGroup {...fieldsHelper.name.formGroup} noMarginBottom>
                  <TextInput {...fieldsHelper.name.textInput} />
                </FormGroup>
              </Col>
              <Col size={6}>
                <FormGroup {...fieldsHelper.surname.formGroup} noMarginBottom>
                  <TextInput {...fieldsHelper.surname.textInput} />
                </FormGroup>
              </Col>
              {mode === 'registration' && (
                <>
                  <Col size={6}>
                    <FormGroup {...fieldsHelper.email.formGroup} noMarginBottom>
                      <TextInput {...fieldsHelper.email.textInput} />
                    </FormGroup>
                  </Col>
                  <Col size={6}>
                    <FormGroup {...fieldsHelper.password.formGroup} noMarginBottom>
                      <PasswordInput {...fieldsHelper.password.passwordInput} />
                    </FormGroup>
                  </Col>
                </>
              )}
              <Col size={6}>
                <FieldArray<string> {...fieldsHelper.phoneNumbers.fieldArray}>
                  {(arrayHelpers) => (
                    <FormGroup
                      {...fieldsHelper.phoneNumbers.formGroup}
                      label={
                        fieldsHelper.phoneNumbers.fieldArray.max === fieldsHelper.phoneNumbers.fieldArray.min
                          ? undefined
                          : fieldsHelper.phoneNumbers.formGroup.label
                      }
                    >
                      {fieldsHelper.phoneNumbers.fields.map(({ formGroup, removeButton, textInput }, index) => (
                        <React.Fragment key={textInput.name}>
                          <FormGroup {...formGroup}>
                            <TextInput {...textInput} />
                          </FormGroup>
                          {fieldsHelper.phoneNumbers.fieldArray.max !== fieldsHelper.phoneNumbers.fieldArray.min && (
                            <p>
                              <Button
                                {...removeButton}
                                disabled={arrayHelpers.removeDisabled}
                                // eslint-disable-next-line react/jsx-no-bind
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                }}
                                variant="blank"
                              />
                            </p>
                          )}
                        </React.Fragment>
                      ))}
                      {fieldsHelper.phoneNumbers.fieldArray.max &&
                        formik.values.phoneNumbers.length < fieldsHelper.phoneNumbers.fieldArray.max && (
                          <p>
                            <Button
                              {...fieldsHelper.phoneNumbers.addButton}
                              disabled={arrayHelpers.pushDisabled}
                              // eslint-disable-next-line react/jsx-no-bind
                              onClick={() => {
                                arrayHelpers.push(getInitialString());
                              }}
                              variant="blank"
                            />
                          </p>
                        )}
                    </FormGroup>
                  )}
                </FieldArray>
              </Col>
            </Row>
          </Div>

          {formik.values.enableBusinessData === 'true' && (
            <Div className="mb-3 pb-3">
              <Row gap={3}>
                <Col size={12}>
                  <FormGroup
                    className="small"
                    {...fieldsHelper.businessType.formGroup}
                    labelClassName="mb-2"
                    noMarginBottom
                  >
                    <Radios {...fieldsHelper.businessType.radios} />
                  </FormGroup>
                </Col>
                <Col size={6}>
                  <FormGroup {...fieldsHelper.company.formGroup} noMarginBottom>
                    <TextInput {...fieldsHelper.company.textInput} />
                  </FormGroup>
                </Col>
                <Col size={6}>
                  <FormGroup {...fieldsHelper.vatNumber.formGroup} noMarginBottom>
                    <TextInput {...fieldsHelper.vatNumber.textInput} />
                  </FormGroup>
                </Col>
                <Col size={6}>
                  <FormGroup {...fieldsHelper.taxCode.formGroup} noMarginBottom>
                    <TextInput {...fieldsHelper.taxCode.textInput} />
                  </FormGroup>
                </Col>
                <Col size={6}>
                  <FormGroup {...fieldsHelper.uniqueCode.formGroup} noMarginBottom>
                    <TextInput {...fieldsHelper.uniqueCode.textInput} />
                  </FormGroup>
                </Col>
                <Col size={6}>
                  <FormGroup {...fieldsHelper.pec.formGroup} noMarginBottom>
                    <TextInput {...fieldsHelper.pec.textInput} />
                  </FormGroup>
                </Col>
              </Row>
            </Div>
          )}
          {(mode === 'registration' || mode === 'updateRequiredFields') && (
            <Row className="mb-3">
              {content?.body.newsletter?.enabled && (
                <Col size={12}>
                  <FormGroup {...fieldsHelper.newsletterConsent.formGroup} noMarginBottom>
                    <Checkbox
                      aria-label={t('accessibility.consents.newsletter')}
                      labelClassName="extra-small"
                      {...fieldsHelper.newsletterConsent.checkbox}
                    />
                  </FormGroup>
                </Col>
              )}
              {content?.body.profiling?.enabled && (
                <Col size={12}>
                  <FormGroup {...fieldsHelper.profilingConsent.formGroup} noMarginBottom>
                    <Checkbox labelClassName="extra-small" {...fieldsHelper.profilingConsent.checkbox} />
                  </FormGroup>
                </Col>
              )}
              {content?.body.marketing?.enabled && (
                <Col size={12}>
                  <FormGroup {...fieldsHelper.marketingConsent.formGroup} noMarginBottom>
                    <Checkbox labelClassName="extra-small" {...fieldsHelper.marketingConsent.checkbox} />
                  </FormGroup>
                </Col>
              )}
              <Col size={12}>
                <FormGroup {...fieldsHelper.privacyConsent.formGroup} noMarginBottom>
                  <Checkbox labelClassName="extra-small" {...fieldsHelper.privacyConsent.checkbox} />
                </FormGroup>
              </Col>
            </Row>
          )}
          {fieldsHelper.buttons.cancel ? (
            <Row>
              <Col size={6}>
                <Div display={'grid'}>
                  <Button {...fieldsHelper.buttons.cancel} />
                </Div>
              </Col>
              <Col size={6}>
                <Div display={'grid'}>
                  <Button {...fieldsHelper.buttons.submit} />
                </Div>
              </Col>
            </Row>
          ) : (
            <Div display={'grid'}>
              {Object.keys(formik.errors).length > 0 && (
                <p className="small text-danger">{t('misc.invalid_form_values')}</p>
              )}
              <Button {...fieldsHelper.buttons.submit} />
            </Div>
          )}
        </>
      )}
    </CustomerFormHelper>
  );
};
